import type { Rng } from './rng.ts';
import { describeRun, type Venue } from './venues.ts';

/**
 * What a citizen is doing all day.
 *
 * DESIGN.md section 8 corrected itself once already on this, and the correction
 * is the design: **the pattern is habit, not employment.** A plate reader
 * network does not reconstruct your work, it reconstructs your life, and it
 * does so out of the boring parts. So jobs are not the substrate here -- routine
 * already is, and the simulated county already runs on it. Jobs are the *reason
 * a player* drives the same roads at the same times, which is the same thing
 * happening to somebody who can notice it.
 *
 * ## Why there is money at all
 *
 * Not for a shop. Money exists because two things in this game needed a price
 * and were free:
 *
 * - **A second car.** A second plate is a second life -- everything the network
 *   knows about you is filed under a plate, so another one is a hole in the
 *   reconstruction the size of whatever you do in it. Handing those out on a
 *   button made evasion trivial and the network toothless. It is the most
 *   expensive thing here, and it should be.
 * - **Registration.** The tag expires on a schedule whether you renew or not,
 *   and an expired tag is a lawful reason to be stopped that has nothing to do
 *   with any list. Renewal costing money makes that a decision rather than a
 *   status effect, and makes being poor a thing the system notices about you.
 *
 * ## The loop, and where it turns
 *
 * Take work, drive it, get paid, and in doing so lay down exactly the kind of
 * legible weekly pattern this whole game is about. Then:
 *
 * > Some of the work is illegal, it pays better, and it is how a player ends up
 * > on the hotlist without the game ever deciding they are a criminal.
 *
 * And the turn is this: **once you are on the list, the pattern you built by
 * working is the thing that finds you.** Not a chase. A query. The player spent
 * hours making themselves reconstructable in order to make rent, and the same
 * record is what the other side reads.
 *
 * Nothing here punishes anybody for taking the illegal work. Section 2 already
 * settled that some players are doing crimes and the game does not flinch from
 * it. What the game does is charge the real price, which is legibility.
 */

export const JOB_KINDS = ['delivery', 'errand', 'ferry', 'collection', 'dropoff'] as const;
export type JobKind = (typeof JOB_KINDS)[number];

export interface Job {
  id: string;
  kind: JobKind;
  /** Where it is picked up, as a node in the road graph. */
  from: number;
  /** Where it has to go. */
  to: number;
  /**
   * The actual places, not the towns.
   *
   * Work used to run from one town to another, which made every job the same
   * job -- nobody delivers to a town. A venue has a name and a kind, which
   * gives the map something specific to draw and gives a week of driving a
   * shape somebody could read off a query. See venues.ts.
   */
  fromVenue: Venue;
  toVenue: Venue;
  /** Plain-language name of each end, for the board. */
  fromName: string;
  toName: string;
  /** What it pays, in whole dollars. */
  pays: number;
  /**
   * Whether doing it is a crime.
   *
   * Named plainly rather than dressed up. A game that called this "grey market
   * work" would be doing the softening its subject matter does not deserve, and
   * a player choosing it should know exactly what they chose.
   */
  illegal: boolean;
  /** What it is, in one line, for somebody reading a board. */
  description: string;
  /** World seconds after which the job is gone. */
  expiresAt: number;
}

/**
 * What things cost.
 *
 * Set against what the work pays rather than against anything real, because
 * this is a game economy and pretending otherwise would be inventing a figure
 * and calling it research. What is deliberate is the *ratio*: a car is roughly
 * forty ordinary jobs, and a registration is two. One of those is a project and
 * the other is an errand, which is the relationship they should have.
 */
export const CAR_PRICE = 2400;
export const REGISTRATION_PRICE = 85;
/** What a player starts with. Enough to renew a tag, not enough to buy a way out. */
export const STARTING_MONEY = 120;

/** What ordinary work pays, per job. */
export const LEGAL_PAY_MIN = 35;
export const LEGAL_PAY_MAX = 90;
/**
 * And what the other kind pays.
 *
 * Between two and three times, which is enough to be tempting and not enough to
 * make the legal work pointless. If illegal work were the only sensible way to
 * afford a second car then the game would be telling every player to commit
 * crimes, which is a different game and a worse argument.
 */
export const ILLEGAL_PAY_MIN = 110;
export const ILLEGAL_PAY_MAX = 260;

/**
 * Chance that an illegal job is reported, per job completed.
 *
 * This is what puts a player on the hotlist, and it is deliberately **not** a
 * function of whether they were seen by a camera. Somebody notices, somebody
 * calls it in, and a plate goes on a list -- which is how it works, and which
 * means the network is not what catches you. The network is what finds you
 * *afterwards*, out of the ordinary driving you did before anybody was looking.
 *
 * That distinction is the whole point and it would be lost if cameras did the
 * catching.
 */
export const REPORTED_CHANCE = 0.22;

/**
 * The furthest a job may take somebody.
 *
 * Two random towns in a twenty-five mile county average nine miles
 * apart, which at road speeds is twenty minutes of driving for one errand --
 * measured at 19.4 km on the first job the board ever offered, for $66. Errands
 * are local, and a loop whose content is the driving still has to be a loop
 * somebody can finish.
 *
 * Four miles is roughly five to eight minutes, which is an errand rather
 * than an expedition.
 */
export const JOB_MAX_METERS = 6000;

/**
 * Which leg of a job somebody is on.
 *
 * Taking a job used to hand you the destination and nothing else, so a delivery
 * was one drive and the package came from nowhere. It is two drives: **go to
 * where the thing is, then take it where it is going.** That is what the work
 * is, and it is also what makes the work generate a pattern -- a courier's day
 * is a chain of specific places, not a chain of destinations.
 */
export type JobStage = 'collect' | 'deliver';

/** How long a job stays on the board, in world seconds. */
export const JOB_LIFETIME = 3600 * 6;

const DESCRIPTIONS: Readonly<Record<JobKind, readonly [string, boolean]>> = Object.freeze({
  delivery: ['Take a package from %FROM% to %TO%.', false],
  errand: ['Pick something up in %FROM% and bring it to %TO%.', false],
  ferry: ['Somebody needs a lift from %FROM% to %TO%.', false],
  collection: ['Collect an envelope in %FROM%. Do not open it. %TO%.', true],
  dropoff: ['Leave a bag in %TO%. It was picked up in %FROM%. No questions.', true],
});

export function isIllegal(kind: JobKind): boolean {
  return DESCRIPTIONS[kind][1];
}

/**
 * Put a job on the board.
 *
 * `places` are named somewhere-to-somewhere pairs the caller has already picked
 * out of the county, because choosing them needs the road graph and this file
 * takes no dependencies.
 */
export function makeJob(
  rng: Rng,
  id: string,
  from: Venue,
  to: Venue,
  now: number,
  kind?: JobKind,
): Job {
  const chosen = kind ?? (JOB_KINDS[rng.int(JOB_KINDS.length)] as JobKind);
  const illegal = isIllegal(chosen);
  const [low, high] = illegal
    ? [ILLEGAL_PAY_MIN, ILLEGAL_PAY_MAX]
    : [LEGAL_PAY_MIN, LEGAL_PAY_MAX];

  return {
    id,
    kind: chosen,
    from: from.node,
    to: to.node,
    fromVenue: from,
    toVenue: to,
    fromName: from.town,
    toName: to.town,
    pays: Math.round(low + rng.next() * (high - low)),
    illegal,
    description: describeRun(chosen, from, to, illegal),
    expiresAt: now + JOB_LIFETIME,
  };
}

/** How many ordinary jobs something costs, for a line a player can read. */
export function inJobs(price: number): number {
  return Math.round(price / ((LEGAL_PAY_MIN + LEGAL_PAY_MAX) / 2));
}
